import { Slide } from '@/components/slides/Slide';
import { SlideContainer } from '@/components/slides/SlideContainer';
import { SlideThumbnailPanel } from '@/components/slides/SlideThumbnailPanel/SlideThumbnailPanel';
import { useMedia } from '@/hooks/useMedia/useMedia';
import { Slide as SlideData } from '@/types/slide';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Skeleton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import * as React from 'react';

type LessonPreviewDialog = {
    open: boolean;
    handleClose: () => void;
    lesson: {
        id: string;
        lessonName: string;
        dateUpdated: string;
        slides: SlideData[];
    };
};

export default function LessonPreviewDialog(props: LessonPreviewDialog) {
    const { isMobile } = useMedia();
    const [selectedSlideIndex, setSelectedSlideIndex] = React.useState(0);
    const slide = React.useMemo(() => {
        return props.lesson.slides?.[selectedSlideIndex] || null;
    }, [props.lesson.slides, selectedSlideIndex]);

    return (
        <Dialog
            fullScreen
            open={props.open}
            onClose={props.handleClose}
            slotProps={{
                paper: {
                    style: {
                        borderRadius: 0,
                        border: 0,
                    },
                },
            }}
        >
            <Box
                component='div'
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    backgroundColor: 'black',
                }}
            >
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        justifyContent: 'space-between',
                        p: 2,
                        alignItems: 'center',
                        backgroundColor: (theme.vars || theme).palette.background.paper,
                        ...theme.applyStyles('dark', { backgroundColor: (theme.vars || theme).palette.grey[800] }),
                    })}
                >
                    <Typography variant='h4'>{props.lesson.lessonName}</Typography>
                    <IconButton onClick={props.handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', margin: '32px 16px', overflow: 'clip', overflowX: isMobile ? 'scroll' : 'clip' }} data-mui-color-scheme='dark'>
                    <SlideContainer
                        padding={0}
                        onNextSlide={
                            props.lesson.slides && selectedSlideIndex < props.lesson.slides.length - 1
                                ? () => {
                                      setSelectedSlideIndex(selectedSlideIndex + 1);
                                  }
                                : undefined
                        }
                        onPreviousSlide={
                            props.lesson.slides && selectedSlideIndex > 0
                                ? () => {
                                      setSelectedSlideIndex(selectedSlideIndex - 1);
                                  }
                                : undefined
                        }
                    >
                        {slide ? <Slide components={slide?.data ?? []} preview /> : <Skeleton variant='rounded' sx={{ height: '100%', width: '100%' }} />}
                    </SlideContainer>
                </div>
                <SlideThumbnailPanel
                    slides={props.lesson.slides}
                    thumbnailIdx={selectedSlideIndex}
                    onThumbnailChange={async (index) => {
                        setSelectedSlideIndex(index);
                    }}
                />
            </Box>
        </Dialog>
    );
}
